import React from "react";
import { motion } from "framer-motion";
import { FadeIn } from "../atoms/FadeIn";
import { WordCycle } from "../atoms/WordCycles";
import { PrimaryButton } from "../atoms/Buttons";
import { SectionFrame } from "../atoms/SectionFrame";
import { ParticleField } from "../atoms/ParticleField";
import { Magnet } from "../atoms/Magnet";
import { AssetPlaceholder } from "../atoms/AssetPlaceholder";
import { ASSETS } from "../../lib/assets";
import { TID } from "../../lib/testIds";

const ROTATING_WORDS = [
    "Motion",
    "Clarity",
    "Attention",
    "Storytelling",
    "Understanding",
    "Impact",
    "Growth",
];

// Cluster — middle head front & lifted; sides slightly tucked back
const HEAD_TWEAKS = [
    { translateY: 14, rotate: -3, z: 1 },
    { translateY: -8, rotate: 0, z: 3 },
    { translateY: 18, rotate: 3, z: 2 },
];

// Single floating founder portrait with magnet hover + idle bob.
function FounderHead({ founder: f, index: i }) {
    const t = HEAD_TWEAKS[i];
    return (
        <div
            className="relative"
            style={{
                zIndex: t.z,
                transform: `translateY(${t.translateY}px) rotate(${t.rotate}deg)`,
            }}
        >
            <Magnet
                padding={90}
                strength={14}
                className="block w-[120px] h-[120px] sm:w-[170px] sm:h-[170px] md:w-[230px] md:h-[230px] lg:w-[280px] lg:h-[280px]"
            >
                <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{
                        duration: 5 + i * 0.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.4,
                    }}
                    className="relative w-full h-full"
                    style={{
                        filter:
                            i === 1
                                ? "drop-shadow(0 30px 60px rgba(0,0,0,0.7))"
                                : "drop-shadow(0 20px 40px rgba(0,0,0,0.55))",
                    }}
                >
                    <AssetPlaceholder
                        src={f.src}
                        alt={f.caption}
                        caption={f.caption}
                        rounded="rounded-full"
                        className="w-full h-full"
                    />
                    {f.src && (
                        <div
                            className="absolute inset-0 rounded-full pointer-events-none"
                            style={{
                                boxShadow:
                                    "0 0 60px rgba(216,255,61,0.10), inset 0 0 0 1px rgba(255,255,255,0.04)",
                            }}
                        />
                    )}
                </motion.div>
            </Magnet>
        </div>
    );
}

export function HeroSection({ onCta }) {
    return (
        <SectionFrame innerClassName="min-h-screen" className="pt-6 md:pt-8">
            <div className="relative h-screen min-h-[820px] overflow-hidden flex flex-col">
                {/* Particle field */}
                <div className="absolute inset-0 pointer-events-none">
                    <ParticleField density={0.45} influence="pull" />
                </div>

                {/* Top row: tag + meta */}
                <FadeIn
                    y={-20}
                    delay={0}
                    className="relative z-30 px-6 md:px-10 pt-6 md:pt-8 flex items-center justify-between gap-4"
                >
                    <img
                        data-testid={TID.heroTag}
                        src="/logo.png"
                        alt="GrowCo Motion"
                        className="h-9 md:h-12 w-auto select-none"
                        draggable={false}
                    />
                    <span
                        className="hidden sm:inline-block text-sm md:text-base uppercase tracking-[0.3em]"
                        style={{ color: "var(--accent)", opacity: 0.85 }}
                    >
                        A motion studio · India
                    </span>
                </FadeIn>

                {/* Center stage — clean vertical stack: heads → headline → rotating word */}
                <div className="relative flex-1 flex flex-col items-center justify-center px-4 md:px-10">
                    {/* 1) Heads row */}
                    <FadeIn
                        delay={0.15}
                        y={20}
                        className="relative z-20 flex items-center justify-center mb-10 sm:mb-12 md:mb-14 lg:mb-16"
                    >
                        <div className="flex items-end justify-center -space-x-8 sm:-space-x-12 md:-space-x-16 lg:-space-x-20">
                            {ASSETS.founders.slice(0, 3).map((f, i) => (
                                <FounderHead key={f.src} founder={f} index={i} />
                            ))}
                        </div>
                    </FadeIn>

                    {/* 2) Headline — two stacked lines, well below the heads */}
                    <FadeIn delay={0.3} y={40} className="relative z-10 w-full text-center">
                        <div
                            data-testid={TID.heroHeading}
                            className="display-text font-black uppercase select-none mx-auto"
                            style={{
                                fontSize: "clamp(2.4rem, 8vw, 8rem)",
                                lineHeight: 0.92,
                                letterSpacing: "-0.035em",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <span className="block">Ideas Deserve</span>
                            <span className="block">
                                <WordCycle
                                    words={ROTATING_WORDS}
                                    interval={2200}
                                    data-testid={TID.heroRotatingWord}
                                />
                            </span>
                        </div>
                    </FadeIn>
                </div>

                {/* Bottom row — subhead + CTA */}
                <div className="relative z-30 px-6 md:px-12 pb-10 md:pb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <FadeIn delay={0.45} y={20} className="max-w-[560px]">
                        <p
                            data-testid={TID.heroSubheadline}
                            className="font-light"
                            style={{
                                color: "var(--text)",
                                fontSize: "clamp(1.1rem, 1.7vw, 1.45rem)",
                                lineHeight: 1.5,
                            }}
                        >
                            Helping businesses explain products, educate teams and launch ideas through motion.
                        </p>
                    </FadeIn>

                    <FadeIn delay={0.6} y={20}>
                        <PrimaryButton
                            label="Let's Make It Move"
                            data-testid={TID.heroCta}
                            onClick={onCta}
                        />
                    </FadeIn>
                </div>

                {/* Bottom hint line */}
                <div
                    className="absolute bottom-2 left-6 md:left-10 right-6 md:right-10 flex items-center justify-between text-sm uppercase tracking-[0.25em] z-30 pointer-events-none"
                    style={{ color: "var(--muted)", opacity: 0.5 }}
                >
                    <span>SCROLL ↓</span>
                    <span>EST. 2024</span>
                </div>
            </div>
        </SectionFrame>
    );
}
